import React, {useState} from "react";
import PageHeadingSection from '../components/PageHeadingSection';
import {formatDate, formatName, getTimeToStart} from '../util/format';
import CaseCardHorizontal from '../components/cards/CaseCardHorizontal';
import {useAuth} from '../util/auth';
import {getNextMonthDate, isSameDay} from '../util/time';
import {fetchScheduledCasesPage} from '../util/pages';
import {useSelector} from 'react-redux';
import {fetchCasesById, fetchCasesByStatus} from '../features/thunks';
import {makeGetCaseByStatus, selectCaseById} from '../features/casesSlice';
import {CaseStatuses} from '../constants';
import CaseParticipantsModal from '../components/Case/CaseParticipantsModal';
import SeoTags from '../components/SeoTags';
import {withUserGetServerSideProps} from '../util/util';
import {wrapper} from '../store/store';
import {selectTimezone} from '../features/timezoneSlice';
import UserCasesList from '../components/UserCasesList';
import partition from 'lodash/partition';
import {useRouter} from 'next/router';
import SigninConditionalRenderer from '../components/SigninConditionalRenderer';
import {Calendar as CalendarNew} from '@/components/ui/calendar';
import { Day, DayProps } from "react-day-picker";
import {HoverCard, HoverCardContent, HoverCardTrigger} from '@/components/ui/hover-card';
import {Typography} from '@/components/ui/typography';
import {cn} from '@/lib/utils';
import {UserAvatar} from '@/components/UserAvatar';
import {selectUserById} from '@/features/usersSlice';
import Link from 'next/link';


const scheduledCasesSelector = makeGetCaseByStatus({
    selectedStatus: [CaseStatuses.SCHEDULED, CaseStatuses.TENTATIVE],
    sortKey: 'scheduled_to',
    userParticipantFilter: undefined,
});

function ScheduledCases(props) {
    const {content = {}} = props;

    return <>
        <SeoTags config={content.seo} />
        <div className="container">
            <div className="grid grid-cols-12 sm:gap-x-8 gap-y-6 mt-8 mb-16">
                <div className="col-span-12 lg:col-span-10 lg:col-start-3 flex flex-col pb-6 mb-6 gap-6 border-b-[1px] border-b-gray-200">
                    <PageHeadingSection
                        backgroundImage={content.heading_background}
                        title={content.heading_title}
                        subtitle={content.heading_subtitle}
                    />
                    <CalendarsList />
                </div>
                <div className="col-span-12 lg:col-start-3 lg:col-span-7 flex flex-col gap-4">
                    <CasesList />
                </div>

                <div className="col-span-12 lg:col-span-3 relative">
                    <div className="sticky top-[100px]">
                        <Typography variant="h5" className="mb-4">
                            My sessions
                        </Typography>
                        <SigninConditionalRenderer
                            title="You must be logged in to access this content"
                            buttonText="Log in to continue"
                        >
                            <UserCasesList showCount={3} fallbackProps={undefined}/>
                        </SigninConditionalRenderer>
                    </div>
                </div>
            </div>
        </div>
    </>;
}

const CaseInTooltip = ({caseItem}) => {
    const timezone = useSelector(selectTimezone);
    const timeToStart = caseItem.scheduled_to - Date.now() / 1000;
    const isStartingSoon = timeToStart < 600;
    const isPlaceholder = caseItem.state === CaseStatuses.TENTATIVE;
    const userId = caseItem?.user;
    const user = useSelector(state => userId ? selectUserById(state, userId) : null);

    return (
        <>
            <div className="flex flex-col items-start gap-2 pb-2 mb-2 border-b-[1px] border-b-gray-200">
                {isStartingSoon && (
                    <p className="text-xs text-teal-200">
                        {getTimeToStart(timeToStart)}
                    </p>
                )}
                {!isStartingSoon && (
                    <p className="text-xs text-muted-foreground">
                        {formatDate({ts: caseItem.scheduled_to, format: 'long', tz: timezone})}
                    </p>
                )}

                {isPlaceholder ? (
                    <span className="font-semibold text-sm text-left">
                            {caseItem.title}
                        </span>
                ) : (
                    <Link href={!isPlaceholder ? `/case/${caseItem.id}` : '#'} passHref>
                        <a className="font-semibold text-sm text-left">
                            {caseItem.title}
                        </a>
                    </Link>
                )}
            </div>
            <div className="flex items-center gap-2">
                <UserAvatar userId={userId} />
                {!!user ? (
                    <div>
                        <p className="text-sm font-medium leading-none">
                            {formatName(user)}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.position}</p>
                    </div>
                ) : null}
            </div>
        </>
    )
};

function DayWithTooltip(props: DayProps) {
    const currentCases = useSelector(scheduledCasesSelector);
    const [myCases, otherCases] = partition(currentCases.filter(it => it.scheduled_to), it => it.user_participant);
    const matchedCases = [...myCases, ...otherCases].filter(it => isSameDay(props.date, new Date(it.scheduled_to * 1000)))

    if (!matchedCases.length) {
        return <Day {...props} />
    }

    return (
        <HoverCard openDelay={300}>
            <HoverCardTrigger asChild>
                <span>
                    <Day {...props} />
                </span>
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
                <div className="flex flex-col gap-4">
                    {matchedCases.map((it, index) => (
                        <div key={it.id}>
                            <CaseInTooltip caseItem={it} />
                        </div>
                    ))}
                </div>
            </HoverCardContent>
        </HoverCard>
    )
}

const CalendarsList = () => {
    const currentCases = useSelector(scheduledCasesSelector);
    const router = useRouter();
    const [myCases, otherCases] = partition(currentCases.filter(it => it.scheduled_to), it => it.user_participant);

    const calendarsCount = 3;

    const nextMonthDates = Array(calendarsCount).fill(undefined).reduce((acc, _, index) => {
        acc.push(index === 0 ? (new Date()) : getNextMonthDate(acc[index - 1]));
        return acc;
    }, []);

    const mySessionsDates = myCases.map((it) => new Date(it.scheduled_to * 1000));
    const availableSessionsDates = otherCases.map((it) => new Date(it.scheduled_to * 1000));

    const thisDay = new Date();
    thisDay.setHours(0, 0, 0, 0);
    return (
        <div className="flex flex-wrap lg:flex-nowrap gap-6">
            {nextMonthDates.map((it, index) => {
                return (
                    <CalendarNew
                        key={index}
                        className={cn("bg-white hidden rounded-lg border-0 shadow-lg 2xl:w-1/3 items-center justify-center", {
                            "flex": index === 0,
                            "lg:flex": index === 1,
                            "xl:flex": index === 2
                        })}
                        classNames={{
                            months: "flex flex-col space-y-4 sm:space-x-4 sm:space-y-0",
                            day_today: '',
                            nav_button_previous: 'hidden',
                            nav_button_next: 'hidden',
                            caption: 'flex pt-1 pb-2 mx-2 border-b-[1px] border-b-muted relative items-center'
                        }}
                        mode="multiple"
                        initialFocus
                        numberOfMonths={1}
                        defaultMonth={it}
                        selected={[]}
                        components={{ Day: DayWithTooltip }}
                        disabled={[
                            { before: thisDay }
                        ]}
                        modifiers={{
                            booked: mySessionsDates,
                            events: availableSessionsDates
                        }}
                        modifiersClassNames={{
                            booked:
                                "bg-accent text-accent-foreground",
                            events:
                                "bg-accent text-accent-foreground",
                        }}
                        onSelect={(selected) => {
                            const selectedDay = selected[0];
                            if (!selectedDay) {
                                return;
                            }
                            const matchedCase = [...myCases, ...otherCases].find(it => isSameDay(selectedDay, new Date(it.scheduled_to * 1000)))

                            if (!matchedCase) {
                                return;
                            }

                            router.push({
                                pathname: `/case/${matchedCase.id}`
                            });
                        }}
                    />
                )
            })}
        </div>
    );
}

const CasesList = () => {
    const [currentCaseId, setCurrentCaseId] = useState(null);
    const cases = useSelector(scheduledCasesSelector);
    const auth = useAuth();

    return <>
        {cases.map(({id}) => {
            return  <CaseCardHorizontal
                key={id}
                onParticipantsClick={auth.isAuthenticated ? () => setCurrentCaseId(id) : undefined}
                caseId={id}
            />
        })}
        <CaseParticipantsModal
            caseId={currentCaseId}
            open={!!currentCaseId}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    setCurrentCaseId(null)
                }
            }}
        />
    </>;
}


export const getServerSideProps = wrapper.getServerSideProps(withUserGetServerSideProps(async (ctx) => {
    const content = await fetchScheduledCasesPage();

    // @ts-ignore
    await ctx.store.dispatch(fetchCasesByStatus({
        orderBy: 'scheduled_to',
        status: [CaseStatuses.SCHEDULED, CaseStatuses.TENTATIVE].join(','),
        userId: ctx?.user?.id,
        onlyFuture: true,
        ctx
    }));

    const caseId = ctx.query.caseId;

    if (caseId) {
        // @ts-ignore
        await ctx.store.dispatch(fetchCasesById({caseId, ctx}));
    }

    return { props: {content} }
}))
export default ScheduledCases;
